/**
 * English copy.
 *
 * Flat keys grouped by area. Flat rather than nested because a missing key is
 * then a single obvious lookup failure rather than a crash three levels down,
 * and because `zh.ts` can be diffed against this file line for line.
 */
export const en = {
  /* ── App chrome ─────────────────────────────────────────────────────── */
  'app.name': 'Backoffice',
  'app.tagline': 'Lottery ops',
  'app.footer.version': 'Lottery backoffice v0.1.0',
  'app.footer.api': 'API',
  'app.footer.connections': '{count} live connection',
  'app.footer.connections_plural': '{count} live connections',
  'app.footer.uptime': 'Uptime {value}',
  'app.footer.demo': '',
  'app.signOut': 'Sign out',
  'app.theme.toLight': 'Switch to light theme',
  'app.theme.toDark': 'Switch to dark theme',
  'app.language': 'Language',
  'app.openNav': 'Open navigation',
  'app.closeNav': 'Close navigation',
  'app.sessionEnds': 'Session ends in {minutes}m',

  /* ── Navigation ─────────────────────────────────────────────────────── */
  'nav.group.watch': 'Watch',
  'nav.group.people': 'People',
  'nav.group.money': 'Money',
  'nav.group.control': 'Control',
  'nav.dashboard': 'Dashboard',
  'nav.draws': 'Draws',
  'nav.games': 'Games',
  'nav.review': 'Review queue',
  'nav.players': 'Players',
  'nav.testPlayers': 'Test players',
  'nav.admins': 'Administrators',
  'nav.sessions': 'Sessions',
  'nav.bets': 'Bets',
  'nav.ledger': 'Wallet ledger',
  'nav.reports': 'Reports',
  'nav.referrals': 'Referral codes',
  'nav.notifications': 'Notifications',
  'nav.settings': 'Settings',
  'nav.permissions': 'Permissions',
  'permissions.note': 'What each role is allowed to do. Changes take effect immediately — an operator signed in right now has their menu and their access updated without reloading.',
  'permissions.superadminNote': 'Superadmin is not listed: they always have every permission, and managing administrators and this screen are not permissions at all. That is what stops a save here locking everyone out.',
  'permissions.module': 'Module',
  'permissions.live': 'live',
  'permissions.editing': 'editing',
  'permissions.changedFromDefault': '(changed from default)',
  'permissions.resetAction': 'Reset to defaults',
  'permissions.resetPrompt': 'Put this role back to the permissions it shipped with?',
  'permissions.discardPrompt': 'Discard your unsaved changes to this role?',
  'permissions.saved': 'Permissions saved',
  'permissions.savedDetail': 'Every {role} console has been updated.',
  'permissions.reset': 'Permissions reset',
  'permissions.resetDetail': 'The role is back to what it shipped with.',
  'permissions.action.view': 'View',
  'permissions.action.create': 'Create',
  'permissions.action.update': 'Update',
  'permissions.action.delete': 'Delete',
  'nav.audit': 'Audit log',

  /* ── Common actions and states ──────────────────────────────────────── */
  'action.search': 'Search',
  'action.reset': 'Reset',
  'action.refresh': 'Refresh',
  'action.cancel': 'Cancel',
  'action.save': 'Save changes',
  'action.create': 'Create',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.remove': 'Remove',
  'action.approve': 'Approve',
  'action.reject': 'Reject',
  'action.copy': 'Copy',
  'action.withdraw': 'Withdraw',
  'action.revoke': 'End session',
  'action.discard': 'Discard changes',
  'action.deletePermanently': 'Delete permanently',

  'common.filters': 'Filters',
  'common.filtersActive': '{count} active',
  'common.filtersUnapplied': 'Unapplied changes — press Search',
  'common.perPage': 'Per page',
  'common.showing': 'Showing',
  'common.of': 'of',
  'common.page': 'Page',
  'common.noRecords': 'No records',
  'common.first': 'First',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.last': 'Last',
  'common.reason': 'Reason',
  'common.reasonHint': 'Recorded in the audit log',
  'common.required': 'Required',
  'common.never': 'Never',
  'common.none': '—',
  'common.justNow': 'just now',
  'common.ago': '{value} ago',
  'common.in': 'in {value}',
  'common.retry': 'Retry',
  'common.loading': 'Loading',
  'common.anyStatus': 'Any status',
  'common.username': 'Username',
  'common.fullName': 'Full name',
  'common.phone': 'Phone',
  'common.balance': 'Balance',
  'common.status': 'Status',
  'common.created': 'Created',
  'common.joined': 'Joined',
  'common.lastSeen': 'Last seen',
  'common.when': 'When',
  'common.amount': 'Amount',
  'common.type': 'Type',
  'common.player': 'Player',
  'common.round': 'Round',
  'common.period': 'Period',
  'common.from': 'From',
  'common.to': 'To',
  'common.state': 'State',
  'common.role': 'Role',
  'common.actions': 'Actions',
  'common.referralCode': 'Referral code',
  /** A round is named by its number — see the draw model's `periodCode`. */
  'common.roundNo': 'Round #{drawNo}',

  /* ── Statuses ───────────────────────────────────────────────────────── */
  'status.pending_review': 'pending review',
  'status.active': 'active',
  'status.banned': 'banned',
  'status.rejected': 'rejected',
  'status.open': 'open',
  'status.locked': 'locked',
  'status.drawn': 'drawn',
  'status.settled': 'settled',
  'status.void': 'void',
  'status.placed': 'placed',
  'status.won': 'won',
  'status.lost': 'lost',
  'status.refunded': 'refunded',
  'status.voided': 'voided',
  'status.success': 'success',
  'status.processing': 'processing',
  'status.pending': 'pending',
  'status.failed': 'failed',
  'status.idle': 'idle',
  'status.expired': 'expired',
  'status.revoked': 'revoked',
  'status.test': 'test',
  'status.manual': 'manual',

  /* ── Sign in ────────────────────────────────────────────────────────── */
  'signIn.title': 'Sign in',
  'signIn.subtitle': 'Staff access only. Every action here is recorded in the audit log.',
  'signIn.username': 'Username',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in',
  'signIn.failed': 'Could not sign in. Check the API is running.',
  'signIn.seeded': '',
  'signIn.expired': 'Your session timed out. Sign in again to continue.',
  'signIn.rejected': 'Your session is no longer valid. Sign in again to continue.',
  'signIn.idle': 'You were signed out after a period of inactivity.',
  'signIn.superseded': 'This account signed in on another device, which ended your session here. If that was not you, change your password.',
  'signIn.displaced': 'Signed in. Another session for this account was open and has been ended.',
  'signIn.displacedDetail': 'If that was not you, change your password now.',

  /* ── Dashboard ──────────────────────────────────────────────────────── */
  'dashboard.liveRound': 'Live round',
  'dashboard.gamePaused': 'Game paused',
  'dashboard.remaining': 'remaining',
  'dashboard.bettingShut': 'betting shut',
  'dashboard.bettingShutsIn': 'betting shuts in {value}',
  'dashboard.justDrawn': 'Result just in',
  'dashboard.resultAnnounce': 'Round {period} drew {numbers}',
  'dashboard.revealQueued': '{count} more result waiting',
  'dashboard.revealQueued_plural': '{count} more results waiting',
  'dashboard.roundsStopped': 'rounds stopped',
  'dashboard.everyInterval': 'every {value}',
  'dashboard.gameSettings': 'Game settings',
  'dashboard.latestNumbers': 'Latest opening numbers',
  'dashboard.allDraws': 'All draws',
  'dashboard.noSettled': 'No settled rounds yet.',
  'dashboard.turnover': 'Turnover',
  'dashboard.paidOut': 'Paid out',
  'dashboard.grossProfit': 'Gross profit',
  'dashboard.awaitingReview': 'Awaiting review',
  'dashboard.settledBets': '{count} settled bets',
  'dashboard.acrossRounds': 'across {count} rounds',
  'dashboard.margin': '{value}% margin',
  'dashboard.needsOperator': 'Needs an operator',
  'dashboard.queueClear': 'Queue is clear',
  'dashboard.waitingApproval': 'Waiting for approval',
  'dashboard.openQueue': 'Open queue',
  'dashboard.nothingWaiting': 'Nothing waiting',
  'dashboard.nothingWaitingDetail': 'New registrations will appear here for review.',
  'dashboard.system': 'System',
  'dashboard.liveConnections': 'Live connections',
  'dashboard.playersOnline': 'Players online',
  'dashboard.staffOnline': 'Staff online',
  'dashboard.onlineNow': 'Online now',
  'dashboard.nobodyOnline': 'Nobody is connected right now.',
  'dashboard.account': 'Account',
  'dashboard.connections': 'Sockets',
  'dashboard.staff': 'Staff',
  'dashboard.responseCache': 'Response cache',
  'dashboard.recentActivity': 'Recent activity',

  /* ── Sessions ───────────────────────────────────────────────────────── */
  'sessions.title': 'Sessions',
  'sessions.eyebrow': 'Access',
  'sessions.note':
    'Every sign-in opens a session. Ending one refuses the holder’s next request — a token on its own can no longer be used.',
  'sessions.principal': 'Account',
  'sessions.kind': 'Kind',
  'sessions.startedAt': 'Started',
  'sessions.expiresAt': 'Ends',
  'sessions.ip': 'IP',
  'sessions.revokeTitle': 'End this session',
  'sessions.revokeBody':
    '{username} will be signed out on their next request. They can sign in again unless the account itself is disabled.',
  'sessions.revokeAll': 'End all sessions',
  'sessions.empty': 'No sessions match',
  'sessions.emptyDetail': 'Adjust the filters and search again.',
  'sessions.ended': 'Session ended',
  'sessions.endedCount': '{count} session(s) ended',
  'sessions.anyKind': 'Players and staff',
  'sessions.admin': 'Staff',
  'sessions.player': 'Players',

  /* ── Games ──────────────────────────────────────────────────────────── */
  'games.playable': 'Playable game',
  'games.running': 'running',
  'games.paused': 'paused',
  'games.pause': 'Pause rounds',
  'games.start': 'Start rounds',
  'games.settings': 'Settings',
  'games.currentRound': 'Current round',
  'games.untilCloses': 'until it closes',
  'games.untilBetsClose': 'until betting stops',
  'games.inLockdown': 'locked down — no more bets',
  'games.opening': 'Opening the next round…',
  'games.noRound': 'No round is open. Start the game to begin the cycle.',
  'games.lastResult': 'Last result',
  'games.numbersPerRound': 'Numbers per round',
  'games.roundEvery': 'Round every',
  'games.lockdown': 'Lockdown before the draw',
  'games.lockdownOff': 'None — bets to the close',
  'games.numberRange': 'Number range',
  'games.odds': 'Odds',
  'games.roundsSoFar': 'Rounds so far',
  'games.inHall': 'In the player hall',
  'games.visible': 'Visible and clickable',
  'games.hidden': 'Hidden',
  'games.otherTiles': 'Other tiles',
  'games.gameHall': 'Game hall',
  'games.otherNote':
    'These appear in the player game hall but nothing runs behind them — the specification has only the lottery playable. They are here so the hall can be arranged, not so they can be started.',
  'games.applyNote':
    'Changes apply to rounds opened from now on. A round already running keeps the settings it started with — rewriting them would change what bets already placed on it were bought against.',
  'games.started': 'Game started',
  'games.startedDetail': 'A new round opens on the next tick.',
  'games.pausedToast': 'Game paused',
  'games.pausedDetail': 'The round already in flight will still be resolved, then rounds stop.',

  'common.entries': '{count} entries',
  'time.dh': '{days}d {hours}h',
  'time.hm': '{hours}h {minutes}m',

  /* ── Relative time ──────────────────────────────────────────────────── */
  'time.seconds': '{value}s',
  'time.minutes': '{value}m',
  'time.hours': '{value}h',
  'time.days': '{value}d',

  /* ── Draws ──────────────────────────────────────────────────────────── */
  'draws.roundDetail': '{positions} numbers · closes {closesAt}',
  'draws.opened': 'Opened',
  'draws.drawn': 'Drawn',
  'draws.source': 'Source',
  'draws.auto': 'auto',
  'draws.you': 'you',

  /* ── Shared vocabulary ───────────────────────────────────────────────── */
  'common.save': 'Save',
  'common.generate': 'Generate',
  'common.optional': 'Optional',
  'common.noMatch': 'Adjust the filters and search again.',
  'common.ledgerHint': 'Stored on the ledger entry and in the audit log',
  'common.typeToConfirm': 'Type {word} to confirm',
  'common.any': 'Any',

  /* ── Form fields ─────────────────────────────────────────────────────── */
  'field.address': 'Address',
  'field.password': 'Password',
  'field.resetPassword': 'Reset password',
  'field.keepPassword': 'Leave blank to keep the current one',
  'field.minPassword': 'At least 8 characters',
  'field.displayName': 'Display name',
  'field.openingBalance': 'Opening balance',
  'field.note': 'Note',
  'field.locale': 'Locale',
  'field.usernameHint': 'Letters, numbers and underscores',
  'field.verifiedByPhone': 'Verified by phone',

  /* ── Table columns ───────────────────────────────────────────────────── */
  'col.lastSignIn': 'Last sign-in',
  'col.reference': 'Reference',
  'col.stake': 'Stake',
  'col.payout': 'Payout',
  'col.registered': 'Registered',
  'col.target': 'Target',
  'col.change': 'Change',
  'col.operator': 'Operator',
  'col.action': 'Action',
  'col.balanceAfter': 'Balance after',
  'col.result': 'Result',
  'col.odds': 'Odds',
  'col.placed': 'Placed',
  'col.pos': 'Pos',
  'col.bet': 'Bet',
  'col.numbers': 'Numbers',
  'col.bets': 'Bets',
  'col.dateTime': 'Date & time',
  'col.winLoss': 'Win / loss',
  'col.code': 'Code',
  'col.usage': 'Usage',
  'col.expires': 'Expires',
  'col.staked': 'Staked',
  'col.paidOut': 'Paid out',

  /* ── Section eyebrows ────────────────────────────────────────────────── */
  'section.people': 'People',
  'section.money': 'Money',
  'section.control': 'Control',
  'section.record': 'Record',
  'section.rounds': 'Rounds',
  'section.review': 'Review',
  'section.credits': 'Credits',
  'section.staff': 'Staff',
  'section.account': 'Account',
  'section.games': 'Games',
  'section.promise': 'Promise',

  /* ── Audit actions ───────────────────────────────────────────────────── */
  'audit.user.approve': 'Approve registration',
  'audit.user.reject': 'Reject registration',
  'audit.user.create': 'Create player',
  'audit.user.update': 'Update player',
  'audit.user.ban': 'Ban player',
  'audit.user.delete': 'Delete player',
  'audit.user.balance_set': 'Set balance',
  'audit.user.top_up': 'Top up',
  'audit.user.withdraw': 'Withdraw',
  'audit.user.history_generate': 'Generate history',
  'audit.bet.update': 'Edit bet',
  'audit.bet.delete': 'Remove bet',
  'audit.draw.auto_result': 'Automatic result',
  'audit.draw.manual_result': 'Manual result',
  'audit.draw.result_edit': 'Change result',
  'audit.draw.void': 'Void round',
  'audit.draw.delete': 'Delete round',
  'audit.transaction.update': 'Edit ledger row',
  'audit.transaction.delete': 'Delete ledger row',
  'audit.admin.create': 'Create administrator',
  'audit.admin.update': 'Update administrator',
  'audit.admin.delete': 'Delete administrator',
  'audit.admin.password_change': 'Change password',
  'audit.session.revoke': 'Revoke session',
  'audit.settings.update': 'Update settings',
  'audit.referral_code.create': 'Create referral code',
  'audit.referral_code.update': 'Update referral code',
  'audit.referral_code.delete': 'Delete referral code',
  'audit.game.create': 'Add game',
  'audit.game.update': 'Update game',
  'audit.game.delete': 'Delete game',
  'audit.testdata.generate': 'Generate test data',

  /* ── Wallet transaction types ────────────────────────────────────────── */
  'tx.top_up': 'Top up',
  'tx.withdrawal': 'Withdrawal',
  'tx.bet_stake': 'Bet stake',
  'tx.bet_payout': 'Bet payout',
  'tx.bet_refund': 'Bet refund',
  'tx.credit_adjustment': 'Credit adjustment',
  'tx.admin_adjustment': 'Admin adjustment (retired)',

  /* ── Bet types ───────────────────────────────────────────────────────── */
  'bet.big': 'Big',
  'bet.small': 'Small',
  'bet.odd': 'Odd',
  'bet.even': 'Even',

  /* ── Players ─────────────────────────────────────────────────────────── */
  'players.title': 'Players',
  'players.new': 'New player',
  'players.empty': 'No players match',
  'players.credit': 'Credit',
  'players.filter.username': 'Username',
  'players.filter.usernamePlaceholder': 'Search username',
  'players.create.title': 'New player',
  'players.create.submit': 'Create player',
  'players.edit.title': 'Edit {name}',
  'players.delete.title': 'Delete {name}',
  'players.delete.warning': 'This removes the player, their bets and their entire wallet history. It cannot be undone.',
  'players.detail.wallet': 'Wallet history',
  'players.detail.bets': 'Bet history',
  'players.detail.walletBalance': 'Wallet balance',
  'players.detail.adjustCredit': 'Adjust credit',
  'players.detail.editProfile': 'Edit profile & status',
  'players.detail.deletePlayer': 'Delete player',
  'players.detail.needAdmin': 'Adding credit and editing need the admin role.',
  'players.detail.noWallet': 'No wallet activity',
  'players.detail.noWalletDetail': 'Credits, stakes and payouts appear here.',
  'players.detail.noBets': 'No bets yet',
  'players.detail.approved': 'Approved',

  /* ── Review queue ────────────────────────────────────────────────────── */
  'review.title': 'Registrations awaiting review',
  'review.empty': 'The queue is clear',
  'review.emptyDetail': 'Every registration has been reviewed. New ones land here automatically.',
  'review.approve': 'Approve',
  'review.reject': 'Reject',
  'review.approveTitle': 'Approve {name}',
  'review.approveSubmit': 'Approve and open account',
  'review.rejectTitle': 'Reject {name}',
  'review.rejectSubmit': 'Reject registration',
  'review.rejectPlaceholder': 'Duplicate registration',

  /* ── Test players ────────────────────────────────────────────────────── */
  'testPlayers.title': 'Players you can shape',
  'testPlayers.subtitle': 'Build a player, then give them a past.',
  'testPlayers.new': 'New test player',
  'testPlayers.empty': 'No players yet',
  'testPlayers.emptyDetail': 'Create one, then generate a betting history for it.',
  'testPlayers.createNote': 'Created active by default so you can start generating history immediately.',
  'testPlayers.history': 'History',
  'testPlayers.historyTitle': 'Generate history for {name}',
  'testPlayers.rounds': 'Rounds to cover',
  'testPlayers.betsPerRound': 'Bets per round',
  'testPlayers.minStake': 'Minimum stake',
  'testPlayers.maxStake': 'Maximum stake',
  'testPlayers.bias': 'Bias toward winning',
  'testPlayers.biasHint': 'Leave as Balanced for an even spread. This picks which selections to make — outcomes are still judged against each round\'s real result.',
  'testPlayers.biasBalanced': 'Balanced (random)',
  'testPlayers.biasMostlyWin': 'Mostly winning (80%)',
  'testPlayers.biasSlightWin': 'Slightly winning (60%)',
  'testPlayers.biasSlightLose': 'Slightly losing (40%)',
  'testPlayers.biasMostlyLose': 'Mostly losing (20%)',

  /* ── Bets ────────────────────────────────────────────────────────────── */
  'bets.title': 'Bet audit',
  'bets.empty': 'No bets match',
  'bets.filter.roundNumber': 'Round number',
  'bets.filter.playerName': 'Player name',
  'bets.filter.playerId': 'Player id',
  'bets.edit.title': 'Edit bet',
  'bets.edit.round': 'Round #{drawNo}',
  'bets.edit.settledWarning': 'This round has been drawn. Saving re-judges the bet against its numbers and moves the player\'s balance to match.',
  'bets.edit.openNote': 'The round is still open. Any stake difference moves through the player\'s wallet like every other balance change.',
  'bets.edit.position': 'Position',
  'bets.edit.positionRange': 'Position — 1 to {count}',
  'bets.edit.positionError': 'Outside this game\'s positions',
  'bets.edit.betType': 'Bet type',
  'bets.edit.stake': 'Stake',
  'bets.edit.stakeError': 'Enter an amount above zero',
  'bets.edit.takesFurther': 'Takes a further {amount} from the player.',
  'bets.edit.returns': 'Returns {amount} to the player.',
  'bets.edit.reasonPlaceholder': 'Corrected after support call',
  'bets.remove.title': 'Remove bet',
  'bets.remove.submit': 'Remove and reverse',
  'bets.remove.heading': 'What this reverses',
  'bets.remove.reasonPlaceholder': 'Duplicate submission reported by the player',

  /* ── Draws ───────────────────────────────────────────────────────────── */
  'draws.title': 'Draws and opening numbers',
  'draws.empty': 'No rounds match',
  'draws.notDrawn': 'Not drawn',
  'draws.roundsMatched': 'Rounds matched',
  'draws.takenFromPlayers': 'Taken from players',
  'draws.returnedToWinners': 'Returned to winners',
  'draws.stakedMinusPaid': 'Staked minus paid out',
  'draws.schedulerNote': 'The scheduler opens one every five minutes.',
  'draws.filter.period': 'Period',
  'draws.filter.result': 'Round result',
  'draws.filter.anyResult': 'Any result — from the house\u2019s side',
  'draws.filter.closedFrom': 'Closed from',
  'draws.filter.closedTo': 'Closed to',
  'draws.setResult': 'Set result',
  'draws.changeResult': 'Change result',
  'draws.setTitle': 'Set result for #{drawNo}',
  'draws.changeTitle': 'Change result for #{drawNo}',
  'draws.correctionEyebrow': 'Correction · round has paid out',
  'draws.setSubmit': 'Set result and settle',
  'draws.changeSubmit': 'Change result and re-judge',
  'draws.freshNote': 'Every bet on this round is judged against these numbers and paid immediately.',
  'draws.paidWarning': 'This round has already paid out. Changing the numbers re-judges every bet on it.',
  'draws.paidDetail': 'Each payout difference moves through the wallet as an adjustment — a bet that was paid and now loses returns the money; one that lost and now wins is paid in full. Balances stay consistent, and players see the movement in their history.',
  'draws.currentResult': 'Current result',
  'draws.newNumbers': 'New numbers',
  'draws.winningNumbers': 'Winning numbers',
  'draws.numbersHint': '{label} — {count} positions, {min} to {max}',
  'draws.unchanged': 'These are the current numbers — change at least one.',
  'draws.acknowledge': 'I understand this reverses and re-applies real payouts, and that it will be recorded against my account.',
  'draws.reasonCorrect': 'Operator draw was misread — corrected',
  'draws.reasonSheet': 'Result taken from the draw sheet',
  'draws.void': 'Void',
  'draws.voidTitle': 'Void round #{drawNo}',
  'draws.voidSubmit': 'Void and refund',
  'draws.voidReason': 'Scheduler fault during the round',
  'draws.deleteTitle': 'Delete round #{drawNo}',
  'draws.deleteSubmit': 'Delete and reverse',
  'draws.deleteReason': 'Duplicate round created during testing',
  'draws.position': 'Position',

  /* ── Wallet ledger ───────────────────────────────────────────────────── */
  'ledger.title': 'Wallet ledger',
  'ledger.empty': 'No ledger rows match',
  'ledger.filter.anyType': 'Any type',
  'ledger.filter.anyStatus': 'Any status',
  'ledger.edit.title': 'Edit ledger row',
  'ledger.delete.title': 'Delete ledger row',
  'ledger.delete.submit': 'Delete and correct balance',

  /* ── Referral codes ──────────────────────────────────────────────────── */
  'referrals.title': 'Referral codes',
  'referrals.empty': 'No referral codes',
  'referrals.emptyDetail': 'Players cannot register without one, so generate at least one.',
  'referrals.generate': 'Generate code',
  'referrals.generateTitle': 'Generate referral code',
  'referrals.codeHint': 'Blank to generate automatically',
  'referrals.codeError': 'At least 4 characters',
  'referrals.codePlaceholder': 'Auto',
  'referrals.noteHint': 'Who it is for — shown only in this console',
  'referrals.notePlaceholder': 'VIP onboarding',
  'referrals.maxUses': 'Maximum uses',
  'referrals.expires': 'Expires',
  'referrals.expiresHint': 'Blank for never',
  'referrals.editTitle': 'Edit {code}',
  'referrals.deleteTitle': 'Delete {code}',
  'referrals.deleteSubmit': 'Delete code',
  'referrals.usedBy': 'Accounts that still exist against this code',
  'referrals.used': 'Used',
  'referrals.remaining': 'Remaining',
  'referrals.usable': 'Usable',
  'referrals.usedUp': 'Used up',
  'referrals.expired': 'Expired',
  'referrals.disabled': 'Disabled',
  'referrals.active': 'Active',
  'referrals.activeHint': 'Can be used to register',
  'referrals.inactiveHint': 'Registration with this code is refused',

  /* ── Administrators ──────────────────────────────────────────────────── */
  'admins.title': 'Administrators',
  'admins.empty': 'No administrators match',
  'admins.new': 'New administrator',
  'admins.createTitle': 'New administrator',
  'admins.editTitle': 'Edit {name}',
  'admins.deleteTitle': 'Delete {name}',
  'admins.accountActive': 'Account is active',
  'admins.accountActiveHint': 'A disabled account cannot sign in.',
  'admins.cannotDeleteSelf': 'You cannot delete your own account',
  'admins.selfNote': 'This is your own account, so role and state are locked — changing either is how an organisation loses its last superadmin. Use Settings to change your password.',
  'admins.role.superadmin': 'Superadmin',
  'admins.role.admin': 'Admin',
  'admins.role.support': 'Support',
  'admins.roleDetail.superadmin': 'Everything, including managing administrators.',
  'admins.roleDetail.admin': 'Everything except managing administrators.',
  'admins.roleDetail.support': 'Read-only, plus deducting credit with a reason.',

  /* ── Games ───────────────────────────────────────────────────────────── */
  'games.title': 'Game hall',
  'games.playableGame': 'Playable game',
  'games.inPlayerHall': 'In the player hall',
  'games.settingsTitle': 'Settings — {name}',
  'games.saveSettings': 'Save settings',
  'games.settingsNote': 'Changes apply to rounds opened from now on. A round already running keeps the settings it started with — rewriting them would change what bets already placed on it were bought against.',
  'games.name': 'Name',
  'games.subtitle': 'Subtitle',
  'games.numbersHint': '1 to 20',
  'games.numbersError': 'Between 1 and 20',
  'games.interval': 'Seconds between rounds',
  'games.intervalHint': 'A round every {value}',
  'games.intervalMin': 'At least 30 seconds',
  'games.intervalError': 'Between 30 and 86,400',
  'games.lockdownHint': 'Betting stops {value} before the numbers',
  'games.lockdownOffHint': '0 — betting runs to the close',
  'games.lockdownMax': 'At most {value} for this interval',
  'games.lockdownError': 'Must be shorter than the round',
  'games.lowest': 'Lowest number',
  'games.highest': 'Highest number',
  'games.rangeError': 'Must be above the lowest number',
  'games.oddsBp': 'Odds (basis points)',
  'games.oddsHint': 'Pays {value}',
  'games.oddsMin': 'At least 10000 (1.00×)',
  'games.oddsError': 'At least 10000',
  /* ── What tapping a hall tile does ─────────────────────────────────── */
  /*
   * One control, four answers. It replaced a "Playable" switch that could only
   * say yes or no, which left every unfinished tile as an inert square with no
   * explanation — and the API derives playability from this, so there is no
   * second switch to keep in step with it.
   */
  'games.interactionLabel': 'Tile status',
  'games.interaction.clickable': 'Playable',
  'games.interaction.disabled': 'Disabled',
  'games.interaction.fake_sign_in': 'Members only',
  'games.interaction.coming_soon': 'Coming soon',
  'games.interactionHint.clickable': 'Opens the game. Round settings appear below.',
  'games.interactionHint.disabled': 'In the hall but inert — nothing happens when a player taps it.',
  'games.interactionHint.fake_sign_in': 'Asks for a sign-in that never succeeds, and tells the player to contact customer service for an account.',
  'games.interactionHint.coming_soon': 'Tells the player the game is on its way.',
  'games.reasonPlaceholder': 'Shortened rounds for the demo',
  'games.pauseRounds': 'Pause rounds',
  'games.startRounds': 'Start rounds',

  /* Hall tiles — add, edit, hide, remove */
  'games.addTile': 'Add tile',
  'games.addTitle': 'Add a game-hall tile',
  'games.addSubmit': 'Add tile',
  'games.addNote': 'Pick what the tile does when a player taps it. A new tile starts on the default round settings, and starting its rounds is a separate action — arranging the hall and running a game are different decisions.',
  'games.noTiles': 'The hall has no other tiles',
  'games.noTilesDetail': 'Add one to fill out the player game hall.',
  'games.roundSettings': 'Round settings',
  'games.slug': 'Slug',
  'games.slugHint': 'Lower-case and hyphenated — it addresses the game in the API',
  'games.slugError': 'Lower-case letters, numbers and single hyphens',
  'games.slugLocked': 'Fixed once created — it addresses the game and keys the player app’s artwork',
  'games.badge': 'Badge',
  'games.badgeHint': 'The small label on the tile, such as New or VIP. Leave empty for none.',
  'games.tileImage': 'Tile image URL',
  'games.tileImageHint': 'Overrides the artwork the player app ships for this slug',
  'games.sortOrder': 'Position in the hall',
  'games.sortOrderHint': 'Ascending — 0 comes first',
  'games.sortOrderError': 'Between 0 and 9,999',
  'games.hiddenToggle': 'Hidden from the hall',
  'games.hiddenHint': 'Players do not receive this tile at all — not over the API, not over the socket',
  'games.shownHint': 'Players see this tile in the game hall',
  'games.hiddenPlayableWarning': 'Hiding does not pause the game. The scheduler keeps opening rounds and a round in flight is still settled, because pulling a tile out of the hall must not strand stakes. Pause it as well if that is what you meant.',
  'games.hiddenBadge': 'hidden',
  'games.inHallBadge': 'in the hall',
  'games.show': 'Show',
  'games.hide': 'Hide',
  'games.hiddenFromConsole': 'Hidden from the hall from the console',
  'games.shownFromConsole': 'Restored to the hall from the console',
  'games.deleteTitle': 'Delete {name}',
  'games.deleteSubmit': 'Delete tile',
  'games.deleteNote': 'Removes the tile from the hall permanently. Hiding it instead takes it out of the player hall and can be undone from this screen at any time.',
  'games.deleteRunning': 'This game is still opening rounds. Pause it before deleting it.',
  'games.deleteHasRounds': 'This game has {count} round(s) of history behind it, so it cannot be deleted — those rounds carry bets and ledger rows that point at it. Hide it instead.',
  'games.deleteReasonPlaceholder': 'Tile was never launched',

  /* ── Reports ─────────────────────────────────────────────────────────── */
  'reports.title': 'Performance',
  'reports.testAccounts': 'Test accounts',
  'reports.excluded': 'Excluded',
  'reports.included': 'Included',
  'reports.clearDates': 'Clear dates',
  'reports.turnover': 'Turnover',
  'reports.turnoverHint': 'Total staked on settled bets',
  'reports.paidOut': 'Paid out',
  'reports.paidOutHint': 'Returned to winners',
  'reports.grossProfit': 'Gross profit',
  'reports.roundsSettled': 'Rounds settled',
  'reports.meaning': 'What the numbers mean',
  'reports.reading': 'Reading this',

  /* ── Notifications ───────────────────────────────────────────────────── */
  'notifications.title': 'Registration review notice',
  'notifications.english': 'English',
  'notifications.chinese': '中文',
  'notifications.supportLink': 'Contact customer service',
  'notifications.reviewUrl': 'Contact URL',
  'notifications.reviewUrlHint': 'Where a player under review is sent to ask about it',
  'notifications.viewingHint': 'Live — this follows the server. Press Edit to change it.',
  'notifications.editingHint': 'Editing. Live updates are paused so nothing overwrites you.',
  'notifications.live': 'live',
  'notifications.editing': 'editing',
  'notifications.contactUrl': 'Contact URL',
  'notifications.contactHint': 'A chat link, form or mailto: address',
  'notifications.reviewNum': 'Contact number',
  'notifications.reviewNumHint': 'The number a player under review can call or message. Leave blank to publish none.',
  'notifications.contactNum': 'Contact number',
  'notifications.contactNumHint': 'The number for top-ups and withdrawals. Leave blank to publish none — players then see the link alone.',
  'notifications.save': 'Save notifications',
  'notifications.discard': 'Discard changes',
  'notifications.players': 'Players',

  /* ── How a session ended ─────────────────────────────────────────────── */
  'sessionEnd.signed_out': 'Signed out',
  'sessionEnd.password_changed': 'Ended by a password change',
  'sessionEnd.account_deleted': 'Account deleted',
  'sessionEnd.account_disabled': 'Account disabled',
  'sessionEnd.revoked_by_admin': 'Ended by an operator',
  'sessionEnd.superseded': 'Signed in on another device',

  /* ── Row actions ─────────────────────────────────────────────────────── */
  'col.actions': 'Actions',
  'action.noneAvailable': 'No actions available on this row',
  'action.noPermission': 'Your role does not have permission for this',
  'action.reroll': 'Reroll',
  'bets.cannotEditSettled': 'A refunded or voided bet is beyond changing',
  'draws.cannotVoid': 'Only a round that has not settled can be voided',
  'draws.cannotSetResult': 'A voided round has no result to set',
  'sessions.cannotRevoke': 'This session has already ended',

  /* ── Rules booklet and upcoming rounds ───────────────────────────────── */
  'notifications.readOnlyHint': 'Read-only — your role does not have permission to change this copy.',
  'rules.title': 'Rules and tutorial',
  'rules.explain': 'The booklet behind the player app\u2019s rules popup \u2014 what the game is and how to play it. Separate from the odds and limits on the Games screen, which the app reads from the game settings: repeat them here as prose and the two drift apart the next time somebody re-prices the game. Both languages are published together.',
  'rules.version': 'version {version}',
  'rules.versionWarning': 'Saving publishes a new version, which raises the popup again for every player \u2014 including those who have already read it. There is no silent correction.',
  'rules.languageTabs': 'Booklet language',
  'rules.docTitle': 'Booklet title',
  'rules.docTitleHint': 'The heading at the top of the popup',
  'rules.intro': 'Opening paragraph',
  'rules.introHint': 'Shown above the sections \u2014 the one thing a player reads if they read nothing else',
  'rules.sections': 'Sections ({count})',
  'rules.sectionNo': 'Section {number}',
  'rules.addSection': 'Add section',
  'rules.noSections': 'No sections yet. The popup will show the title and opening paragraph alone.',
  'rules.heading': 'Heading',
  'rules.body': 'Body',
  'toast.rulesPublished': 'Notices saved and the rules booklet published as version {version}.',
  'upcoming.title': 'Upcoming round numbers',
  'upcoming.explain': 'Winning numbers are drawn ahead of time and queued, so you can see what is coming and change it before it is played. The first row is the round counting down now; the rest are estimates that shift if a round is voided or the game is paused.',
  'upcoming.round': 'Round',
  'upcoming.drawsAt': 'Draws',
  'upcoming.numbers': 'Numbers',
  'upcoming.origin': 'Origin',
  'upcoming.live': 'now',
  'upcoming.estimated': 'estimated',
  'upcoming.manual': 'Set by hand',
  'upcoming.auto': 'Generated',
  'upcoming.stale': 'Outside the current number range \u2014 this round would draw fresh numbers instead. Edit or reroll it.',
  'upcoming.reroll': 'Reroll',
  'upcoming.paused': 'Game paused \u2014 times are guesses',
  'upcoming.empty': 'Nothing queued yet.',
  'upcoming.editTitle': 'Numbers for round #{drawNo}',
  'upcoming.editLive': 'This is the round players are betting on right now ({period}). These numbers are what it will open on.',
  'upcoming.editProjected': 'This round has not opened yet. Which round it turns out to be shifts if a round is voided or the game is paused.',
  'upcoming.rangeHint': '{count} number(s), each between {min} and {max}.',
  'upcoming.reasonPlaceholder': 'Scripted result for the launch demo',
  'upcoming.save': 'Save numbers',
  'toast.upcomingSaved': 'Round numbers saved',
  'toast.upcomingSavedDetail': 'Round #{drawNo} will open on the numbers you set.',
  'toast.upcomingRerolled': 'Numbers redrawn',
  'toast.upcomingRerolledDetail': 'Round #{drawNo} is back on generated numbers.',
  'error.upcoming': 'Could not change the queued numbers',

  /* ── Settings ────────────────────────────────────────────────────────── */
  'settings.yourAccount': 'Your account',
  'settings.changePassword': 'Change password',
  'settings.current': 'Current password',
  'settings.new': 'New password',
  'settings.confirm': 'Confirm new password',
  'settings.mismatch': 'The two passwords do not match',

  /* ── Audit log ───────────────────────────────────────────────────────── */
  'audit.title': 'Operation log',
  'audit.empty': 'Nothing recorded yet',
  'audit.emptyDetail': 'Admin actions appear here as they happen.',
  'audit.scopedToYou': 'You are seeing your own actions. Only a superadmin can see every operator’s trail.',
  'audit.note': 'Every administrator action is recorded here. The log is append-only — there is no route in the API to edit or delete an entry, and none in this console.',
  'audit.noChange': 'No field changed',
  'audit.filter.anyAction': 'Any action',
  'audit.filter.anyOperator': 'Any operator',
  'audit.filter.targetId': 'Target id',
  'audit.filter.recordId': 'Record id',

  /* ── Credit dialog ───────────────────────────────────────────────────── */
  'credit.topUp': 'Top up',
  'credit.setBalance': 'Set balance',
  'credit.withdraw': 'Withdraw',
  'credit.operation': 'Credit operation',
  'credit.title': '{mode} — {name}',
  'credit.currentBalance': 'Current balance',
  'credit.afterTopUp': 'After top-up',
  'credit.afterWithdrawal': 'After withdrawal',
  'credit.newBalance': 'New balance',
  'credit.amountToAdd': 'Amount to add',
  'credit.amountToDeduct': 'Amount to deduct',
  'credit.exactHint': 'An exact amount, not an amount to add or subtract.',
  'credit.topUpBlurb': 'Adds credit after money has come in. It records a deposit in the player\'s wallet history, so they see it the way they expect rather than as an unexplained adjustment.',
  'credit.withdrawBlurb': 'Deducts credit after you have paid the player out. It records a withdrawal in their wallet history, so they see it the way they expect rather than as an unexplained adjustment.',
  'credit.setBlurb': 'Sets the credit to whatever figure you type. For money that actually moved, use top up or withdraw instead — those read as a deposit and a payout in the player\'s history.',
  'credit.reasonRef': 'Reason or reference',
  'credit.topUpPlaceholder': 'Bank transfer received, ref 8841-2290',
  'credit.withdrawPlaceholder': 'Paid out by bank transfer, ref 4471',
  'credit.setPlaceholder': 'Corrected after duplicate credit',
  'credit.recordTopUp': 'Record top-up',
  'credit.recordWithdrawal': 'Record withdrawal',
  'credit.tooMuch': 'More than the {amount} available',
  'credit.nonNegative': 'Enter a valid, non-negative amount',
  'credit.willCredit': 'This will credit {amount} and record it in the ledger as an adjustment.',
  'credit.willDebit': 'This will debit {amount} and record it in the ledger as an adjustment.',

  /* ── Status filter options ──────────────────────────────────────── */
  'statusOption.any': 'Any status',
  'statusOption.pending_review': 'Pending approval',
  'statusOption.active': 'Active',
  'statusOption.banned': 'Banned',
  'statusOption.rejected': 'Rejected',

  /* ── Review queue ────────────────────────────────────────────────────── */
  'review.approveBlurb': 'Approving activates the account so {name} can sign in and bet. The opening balance is optional — it is recorded in the ledger and the audit log like any other credit.',
  'review.thisPlayer': 'this player',
  'review.rejectBlurb': 'The account stays on record but cannot sign in. The reason is stored on the account and written to the audit log.',
  'review.approvedToast': 'Account approved',
  'review.approvedDetail': '{name} can now sign in and place bets.',
  'review.rejectedToast': 'Registration rejected',
  'review.rejectedDetail': '{name} cannot sign in.',

  /* ── Review queue errors ─────────────────────────────────────────────── */
  'review.approveError': 'Could not approve this account',
  'review.rejectError': 'Could not reject this registration',
  'review.creditsOnApproval': 'Credits {amount} on approval',
  'review.noOpening': 'Leave at 0.00 for none',

  /* ── Credit dialog errors ────────────────────────────────────────────── */
  'credit.failed': 'Could not complete this credit change',
  'credit.topUpToast': 'Top-up recorded',
  'credit.topUpDetail': '{amount} added — {name} now holds {balance}.',
  'credit.withdrawToast': 'Withdrawal recorded',
  'credit.withdrawDetail': '{amount} deducted — {name} now holds {balance}.',
  'credit.setToast': 'Balance updated',
  'credit.setDetail': '{name} set to {balance} ({delta}).',

  /* ── Bets ────────────────────────────────────────────────────────────── */
  'bets.filter.placedFrom': 'Placed from',
  'bets.filter.placedTo': 'Placed to',
  'bets.updatedToast': 'Bet updated',
  'bets.stakeDelta': 'Stake {amount}.',
  'bets.nowStatus': 'Now {status}.',
  'bets.nowStatusPaying': 'Now {status}, paying {amount}.',
  'bets.updateError': 'Could not update the bet',
  'bets.removedToast': 'Bet removed',
  'bets.removedDetail': 'The stake was returned and any payout reversed.',
  'bets.removeError': 'Could not remove the bet',

  /* ── Player dialogs ──────────────────────────────────────────────────── */
  'players.whatGoesWithThem': 'What goes with them',

  /* ── Administrator roles ─────────────────────────────────────────────── */
  'admins.roleScope.support': 'Review queue and read-only views',
  'admins.roleScope.admin': 'Balances, bets, draws, settings',
  'admins.roleScope.superadmin': 'Everything, including staff accounts',

  /* ── Administrator filters ───────────────────────────────────────────── */
  'admins.anyRole': 'Any role',

  /* ── Option lists ────────────────────────────────────────────────────── */
  'ledger.anyType': 'Any type',
  'txStatus.success': 'Success',
  'txStatus.processing': 'Processing',
  'txStatus.pending': 'Pending',
  'txStatus.failed': 'Failed',
  'txStatus.rejected': 'Rejected',
  'drawStatus.open': 'Open',
  'drawStatus.locked': 'Locked',
  'drawStatus.drawn': 'Drawn',
  'drawStatus.settled': 'Settled',
  'drawStatus.void': 'Void',
  'draws.filter.houseWon': 'House won',
  'draws.filter.houseLost': 'House lost',
  'draws.filter.breakEven': 'Break even',
  'admins.anyState': 'Any state',
  'referrals.anyState': 'Any state',
  'referrals.searchCode': 'Search code',

  /* ── Toasts, errors and misc ─────────────────────────────────────────── */
  'toast.playerCreated': 'Player created',
  'toast.playerUpdated': 'Player updated',
  'toast.playerDeleted': 'Player deleted',
  'toast.adminCreated': 'Administrator created',
  'toast.adminUpdated': 'Administrator updated',
  'toast.adminDeleted': 'Administrator deleted',
  'toast.codeCreated': 'Code created',
  'toast.codeUpdated': 'Code updated',
  'toast.codeDeleted': 'Code deleted',
  'toast.rowUpdated': 'Ledger row updated',
  'toast.rowDeleted': 'Ledger row deleted',
  'toast.balanceCorrected': 'Balance corrected to {amount}.',
  'toast.gameUpdated': 'Game updated',
  'toast.gameSettingsNote': 'The next round to open uses these settings.',
  'toast.tileCreated': 'Tile added',
  'toast.tileCreatedDetail': '{name} is in the game hall.',
  'toast.tileDeleted': 'Tile deleted',
  'toast.tileDeletedDetail': '{name} is no longer in the game hall.',
  'toast.tileHidden': 'Tile hidden',
  'toast.tileHiddenDetail': 'Players no longer receive it. Unhide it from this screen at any time.',
  'toast.tileShown': 'Tile restored',
  'toast.tileShownDetail': 'It is back in the player game hall.',
  'toast.gameStarted': 'Game started',
  'toast.gamePaused': 'Game paused',
  'toast.historyCreated': 'History created',
  'toast.notificationsSaved': 'Notifications saved',
  'toast.notificationsDetail': 'Players see the new copy on their next request.',
  'toast.passwordChanged': 'Password changed',
  'toast.passwordDetail': 'Sign in again with your new password.',
  'toast.resultSet': 'Result set',
  'toast.resultChanged': 'Result changed',
  'toast.noBets': 'No bets were placed on this round.',
  'toast.roundVoided': 'Round voided',
  'toast.roundDeleted': 'Round deleted',
  'toast.copyFailed': 'Could not copy',
  'toast.copyManually': 'Select the code and copy it manually.',
  'error.createPlayer': 'Could not create the player',
  'error.updatePlayer': 'Could not update the player',
  'error.deletePlayer': 'Could not delete the player',
  'error.createAdmin': 'Could not create the administrator',
  'error.updateAdmin': 'Could not update the administrator',
  'error.deleteAdmin': 'Could not delete the administrator',
  'error.createCode': 'Could not create the code',
  'error.updateCode': 'Could not update the code',
  'error.deleteCode': 'Could not delete the code',
  'error.updateRow': 'Could not update the row',
  'error.deleteRow': 'Could not delete the row',
  'error.saveGame': 'Could not save the game',
  'error.deleteGame': 'Could not delete the tile',
  'error.changeGame': 'Could not change the game',
  'error.buildHistory': 'Could not generate the history.',
  'error.changePassword': 'Could not change your password',
  'error.save': 'Could not save',
  'error.setResult': 'Could not set the result',
  'error.voidRound': 'Could not void the round',
  'error.deleteRound': 'Could not delete the round',
  'error.endSession': 'Could not end the session',
  'error.endSessions': 'Could not end the sessions',
  'error.passwordsDiffer': 'Passwords do not match',
  'error.minCode': 'At least 4 characters',
  'error.atLeastMinimum': 'Must be at least the minimum',
  'games.startedFromConsole': 'Started from console',
  'games.pausedFromConsole': 'Paused from console',
  'dashboard.noActivity': 'No activity yet',
  'dashboard.openingNumbers': 'Opening numbers',

  /* ── Prose ───────────────────────────────────────────────────────────── */
  'players.deleteNote': 'Their rows are removed rather than orphaned — leaving them behind would distort the reports. The audit entry keeps the record of this deletion. It cannot be undone.',
  'admins.selfLocked': 'This is your own account, so role and state are locked — changing either is how an organisation locks itself out. Use Settings to change your password.',
  'admins.deleteNote': 'Removes the account. Their entries in the audit log stay — the record of what they did is not theirs to take with them.',
  'bets.removeNote': 'History is not deleted. The bet is marked voided and the balance is corrected with compensating entries, so the ledger still reconciles.',
  'games.tilesNote': 'The rest of the hall. These are rows you own from here — add one, rename it, reorder it, hide it or remove it. A tile that is not playable still appears to players; it simply does not open, which is a display decision rather than a broken link.',
  'ledger.amountFixed': 'The amount is deliberately fixed — a ledger whose figures can be rewritten is not a ledger. Correct a balance from the player screen instead.',
  'notifications.reviewNote': 'Shown after a player submits a registration, and again if they try to sign in before approval.',
  'notifications.supportNote': 'Where the Top Up and Withdraw buttons send players. There is no deposit or withdrawal endpoint — both open this link instead.',
  'referrals.deleteNote': 'Players list will stop showing which code they used.',
  'referrals.disableInstead': 'Disabling it instead refuses new registrations while keeping that history.',
  'settings.currentRequired': 'Your current password is required even though you are signed in — it is what stops someone using an unattended screen.',
  'testPlayers.realNote': 'Accounts made here are real players — they appear in reports, ledgers and the player list like any other.',
  'testPlayers.historyNote': 'Generated history writes genuine bets against rounds that have already settled.',
  'testPlayers.buildNote': 'Places bets for this player across rounds that have already settled. Stakes are debited and winnings credited through the wallet, so the ledger stays consistent.',
  'testPlayers.buildTitle': 'Build history — {name}',
  'draws.voidNote': 'Cancels the round and refunds every stake on it. A round that has already settled cannot be voided — change its result instead.',
  'players.statusNow': 'Status is now {status}.',
  'common.sortBy': 'Sort by {field}',
  'referrals.alreadyUsed': 'Already used {count} times',

  /* ── Prose, part two ─────────────────────────────────────────────────── */
  'bets.edit.drawnNote': 'This round has been drawn. Saving re-judges the bet against its numbers and moves the player\'s balance to match.',
  'bets.edit.stillOpen': 'The round is still open. Any stake difference moves through the player\'s wallet like every other balance change.',
  'draws.paidWarn': 'This round has already paid out. Changing the numbers re-judges every bet on it.',
  'draws.paidExplain': 'Each payout difference moves through the wallet as an adjustment — a bet that was paid and now loses returns the money; one that lost and now wins is paid in full. Balances stay consistent, and players see the movement in their history.',
  'draws.freshExplain': 'Every bet on this round is judged against these numbers and paid immediately.',
  'ledger.amountFixedNote': 'The amount is deliberately fixed — a ledger whose figures can be retyped is not a ledger. Its annotation, its status and when it happened are editable; to change the money, delete this row or make a fresh adjustment.',
  'notifications.reviewExplain': 'Shown after a player submits a registration, and again if they try to sign in before being approved. It is the only message they get in between, so it should tell them what happens next.',
  'notifications.supportExplain': 'Shown to a player in three places: the popup after they register, Top Up, and Withdraw. There is no payment gateway — both funding paths end in a conversation with your team, so this message and link are the whole route. The player app reads them from `GET /contact`, and saving here reaches every open client immediately.',
  'referrals.signupsNote': '{count} player(s) registered with this code. They keep their accounts, but the reference goes stale and the Players list will stop showing which code they used.',
  'referrals.disableExplain': 'Disabling it instead refuses new registrations while keeping that history readable — use Edit → State if that is what you want.',

  /* ── Prose, part three ───────────────────────────────────────────────── */
  'settings.currentExplain': 'Your current password is required even though you are signed in — it is what stops an unattended terminal from becoming a permanent handover of your account. You will be signed out afterwards.',
  'testPlayers.realExplain': 'Accounts made here are real players — they appear in reports, aggregates and the players list exactly like anyone who registered. Generated history writes genuine bets against rounds that have already settled, and every stake and payout moves through the wallet, so the balance still reconciles afterwards.',
  'testPlayers.buildExplain': 'Places bets for this player across rounds that have already settled. Stakes are debited and winnings credited through the wallet, so the ledger stays consistent — the player simply ends up with a past.',
  'draws.voidExplain': 'Cancels the round and refunds every stake on it. A round that has already settled cannot be voided.',
  'draws.betsOnRound': '{count} bets',
  'draws.currentlyOnRound': 'currently on this round.',
  'draws.deleteRoundTitle': 'Delete round #{drawNo}',
  'draws.voidRoundTitle': 'Void round #{drawNo}',

  /* ── Final strings ───────────────────────────────────────────────────── */
  'betStatus.placed': 'Placed',
  'betStatus.won': 'Won',
  'betStatus.lost': 'Lost',
  'betStatus.refunded': 'Refunded',
  'betStatus.voided': 'Voided',
  'reports.turnoverExplain': 'counts stakes on bets that have settled — bets on the round currently open are not included, because their outcome is unknown.',
  'reports.profitExplain': 'is turnover minus payouts. Every bet type is an even-money proposition paying 1.98×, so the expected margin is 1%. A short period will swing either side of that; a long one should converge on it.',
  'reports.testExplain': 'are excluded by default. Including them mixes generated demo activity into the figures, which is useful for checking the generator and misleading for anything else.',
  'reports.marginOfTurnover': '{value}% of turnover',

  /* ── Void the running round ──────────────────────────────────────────── */
  'games.voidRound': 'Void round',
  'games.voidTitle': 'Void the running round',
  'games.voidBlurb': 'Cancels {period} and opens a fresh round straight away on the settings as they now stand. Every stake on the cancelled round is refunded, so nobody is left betting against a result that will never be drawn.',
  'games.voidWhy': 'Use it after changing the settings above — those only affect rounds opened afterwards, so the running one would otherwise finish on the old configuration.',
  'games.voidReasonPlaceholder': 'Shortened the interval — restarting on the new settings',
  'games.voidSubmit': 'Void and restart',
  'games.voidBets': '{count} bet(s) currently on this round',
  'games.voidNoRound': 'No round is running, so there is nothing to void.',
  'toast.roundVoidedRestarted': 'Round voided and restarted',
  'toast.roundVoidedDetail': '{count} stake(s) refunded. {period} is now open.',

  /* ── Straight number bets ────────────────────────────────────────────── */
  'bet.number': 'Number',
  'bets.edit.pickedNumber': 'Number',
  'bets.edit.pickedRange': 'Number — {min} to {max}',
  'bets.edit.pickedError': 'Pick a number in this round’s range',
  'bets.edit.numberOdds': 'Wins on any number in the same half · pays {odds}',

  /* ── Registration details, player history search and timestamp edits ─── */
  'field.bankName': 'Bank',
  'field.bankAccountNo': 'Bank account number',
  'field.bankLoadFailed': 'Could not load the bank list. Reopen this dialog to try again.',
  'field.bankUnlisted': '{name} (not in the current list)',
  'players.detail.registration': 'Registration details',
  'players.detail.registrationNote': 'Everything the player entered when they signed up.',
  'players.filter.player': 'Player',
  'players.filter.playerPlaceholder': 'Search by username',
  'bets.edit.placedAt': 'Placed at',
  'bets.edit.placedAtHint': 'Moves the bet in the history. The stake, the odds and the outcome are untouched.',
  'bets.edit.placedAtError': 'Enter a valid date and time',
  'ledger.edit.happenedAt': 'Date & time',
  'ledger.edit.happenedAtHint': 'When the movement happened. The amount and the balance snapshot are left as they are.',
  'ledger.edit.happenedAtError': 'Enter a valid date and time',

  /* ── Generated history: the profile form ─────────────────────────────── */
  'day.short.0': 'Sun',
  'day.short.1': 'Mon',
  'day.short.2': 'Tue',
  'day.short.3': 'Wed',
  'day.short.4': 'Thu',
  'day.short.5': 'Fri',
  'day.short.6': 'Sat',
  'history.section.when': 'When they played',
  'history.section.how': 'How they played',
  'history.from': 'From',
  'history.to': 'To',
  'history.pattern': 'Playing pattern',
  'history.pattern.weekdays': 'Days of the week',
  'history.pattern.interval': 'Every few days',
  'history.days': 'Days played',
  'history.daysHint': 'Pick the days they placed bets on.',
  'history.daysError': 'Choose at least one day',
  'history.intervalDays': 'A bet every',
  'history.intervalDays.suffix': 'days',
  'history.intervalDaysHint':
    'Days between playing days, drawn afresh each time — 4 to 6 wanders, 6 to 6 is exact.',
  'history.betsPerDay': 'Bets per playing day',
  'history.betsPerDayHint': 'Set both the same for exactly that many.',
  'history.rangeError': 'The second figure must be at least the first',
  'history.minStake': 'Smallest stake',
  'history.maxStake': 'Largest stake',
  'history.winRate': 'Win rate',
  'history.winRateHint': 'Exact, not approximate: 70% of the bets win. Each is still judged against its round’s real numbers.',
  'history.depositMin': 'Smallest deposit',
  'history.depositMax': 'Largest deposit',
  'history.autoTopUp': 'Raise a day\'s deposit when it falls short',
  'history.autoTopUpHint': 'When a day\'s bets, or its closing withdrawal, need more than the deposit drawn. Off refuses the profile instead.',
  'history.withdrawalShare': 'Share of profit taken',
  'history.withdrawalShareHint': 'Each withdrawal is the day\'s deposit back plus this share of what the player is up, so it is always more than the deposit.',
  'history.withdrawalFloor': 'Smallest withdrawal',
  'history.withdrawalFloorHint': 'Every deposit day withdraws at least this much.',

  /* ── Generated history: the dialogs ──────────────────────────────────── */
  'history.title': 'History for {name}',
  'history.explain': 'Writes real bets and real wallet rows across the dates you choose. Everything here is exact: the counts and the win rate are worked out for the whole run before anything is written, so a 70% win rate means exactly 70%. Every playing day reads Deposit → bets → Withdraw. Stakes and payouts move through the wallet like any other play, so the balance still reconciles afterwards.',
  'history.enable': 'Give this player a betting history',
  'history.enableHint': 'Configure it here and it is saved with the account, ready to run or adjust later.',
  'history.generateNow': 'Generate it as soon as the account is created',
  'history.preview': 'Preview',
  'history.previewTitle': 'What will be written',
  'history.previewBets': '{bets} bets across {days} playing days, {wins} of them winning',
  'history.previewDeposits': '{count} deposits',
  'history.previewWithdrawals': '{count} withdrawals',
  'history.save': 'Save profile',
  'history.saveAndRun': 'Save and regenerate',
  'history.run': 'Generate now',
  'history.clear': 'Clear generated history',
  'history.clearConfirm': 'Remove the bets and wallet rows previous runs wrote for this player? Play the account did itself is untouched.',
  'history.replaceNote': 'Regenerating replaces what earlier runs wrote, so the record matches the profile rather than holding both.',
  'history.lastRun': 'Last generated {when}',
  'history.neverRun': 'Never generated',
  'history.noProfile': 'No history profile',
  'history.working': 'Writing history — a long range takes a moment.',
  'history.section.money': 'Deposits and withdrawals',
  'history.moneyExplain': 'A deposit day opens with one deposit and closes with one withdrawal larger than it. Other playing days are bets and wins only. Days without bets have neither.',
  'history.openingBalanceHint': 'Added to the first generated deposit, so the history reads in order and ends on a withdrawal.',
  'history.cashFlow': 'Deposit & withdraw chance per day (%)',
  'history.cashFlowHint': 'Each playing day rolls this chance on its own. Hit: Deposit → bets and wins → Withdraw, always withdrawing more than was deposited. Miss: bets and wins only. 100% = every day. The first day always deposits, and a day with nothing left to bet with deposits too.',
  'history.cashFlowError': 'Enter 0 to 100',
  'history.order.cashDays': 'Deposit day ({percent}% chance)',
  'history.order.betDays': 'Otherwise ({percent}% chance)',
  'history.order.hint': 'Every playing day follows one of these orders. Losing bets have no win record.',
  'history.section.day': 'The day',
  'history.window': 'Active hours',
  'history.windowHint': 'Every bet, win, deposit and withdrawal falls between these times.',
  'history.windowOvernight': 'Runs past midnight — the session belongs to the day it starts on.',
  'history.windowError': 'Enter two different times',
  'history.timeZone': 'Time zone',
  'history.timeZoneHint': 'The times above are read in this zone, not the server\'s.',
  'history.settle': 'When wins appear',
  'history.settle.each': 'After each bet',
  'history.settle.together': 'Bets first, then wins',
  'history.gap': 'Pause between records',
  'history.gap.suffix': 'min',
  'history.gapHint': 'On top of waiting for each round\'s result. Shortened automatically if a day would not fit the active hours.',
  'history.step.deposit': 'Deposit',
  'history.step.withdraw': 'Withdraw',
  'history.step.bet': 'Bet {n}',
  'history.step.win': 'Win {n}',
  'history.previewWindow': 'All between {start} and {end} ({zone})',
  'history.sampleTitle': 'How the first days will read',
  'history.sample.betWon': 'Bet (wins)',
  'history.sample.betLost': 'Bet (loses)',
  'history.sample.win': 'Win',
  'toast.historyBuilt': 'History generated',
  'toast.historyBuiltDetail': '{bets} bets ({won} won), {deposits} deposits, {withdrawals} withdrawals. Balance now {balance}.',
  'toast.historyReplaced': 'Replaced {bets} earlier bets and {rows} wallet rows.',
  'toast.historyCleared': 'Generated history cleared',
  'toast.historyClearedDetail': '{bets} bets, {rows} wallet rows and {rounds} rounds removed.',
  'toast.profileSaved': 'Profile saved',
  'status.backfilled': 'Generated',
} as const;

export type TranslationKey = keyof typeof en;
